import React from 'react';
import { Search, ZoomIn, ZoomOut, Maximize2, Globe, Heart, Users, ArrowLeft } from 'lucide-react';
import { SUPPORTED_LANGUAGES } from '../../data/kinshipTranslations';

export default function TreeControls({
  searchQuery,
  onSearchChange,
  selectedLanguage,
  onLanguageChange,
  showLanguageDropdown,
  onToggleLanguageDropdown,
  zoomLevel,
  onZoomIn,
  onZoomOut,
  onResetView,
  showBackButton,
  onBack,
}) {
  return (
    <div className="bg-white border-b border-gray-200 px-4 py-2 flex items-center justify-between shrink-0">
      <div className="flex items-center gap-3">
        {/* Back button */}
        {showBackButton && (
          <button
            onClick={onBack}
            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors text-gray-600"
            title="Go back"
          >
            <ArrowLeft size={16} />
          </button>
        )}
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Find a family member..."
            className="pl-8 pr-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 w-36 sm:w-52"
          />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <div className="hidden md:flex items-center gap-3 mr-4">
          <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-full bg-blue-100 border border-blue-300" /><span className="text-[10px] text-gray-500">Male</span></div>
          <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-full bg-pink-100 border border-pink-300" /><span className="text-[10px] text-gray-500">Female</span></div>
          <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-full bg-gray-200 border border-gray-300" /><span className="text-[10px] text-gray-500">Deceased</span></div>
          <Heart size={12} className="text-red-400" /><span className="text-[10px] text-gray-500">Couple</span>
          <Users size={12} className="text-indigo-400" /><span className="text-[10px] text-gray-500">View Family</span>
        </div>

        {/* Language selector */}
        <div className="relative">
          <button
            onClick={onToggleLanguageDropdown}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors text-sm"
            title="Change language"
          >
            <Globe size={14} className="text-gray-500" />
            <span className="text-gray-700 font-medium">{SUPPORTED_LANGUAGES.find(l => l.code === selectedLanguage)?.flag} {SUPPORTED_LANGUAGES.find(l => l.code === selectedLanguage)?.label}</span>
          </button>
          {showLanguageDropdown && (
            <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1 min-w-[160px]">
              {SUPPORTED_LANGUAGES.map(lang => (
                <button
                  key={lang.code}
                  onClick={() => { onLanguageChange(lang.code); }}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 transition-colors flex items-center gap-2 ${selectedLanguage === lang.code ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-gray-700'}`}
                >
                  <span>{lang.flag}</span>
                  <span>{lang.label}</span>
                  {selectedLanguage === lang.code && <span className="ml-auto text-indigo-600">✓</span>}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="hidden sm:flex items-center gap-2">
          <button onClick={onZoomOut} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors" title="Zoom out"><ZoomOut size={16} className="text-gray-600" /></button>
          <span className="text-xs text-gray-500 min-w-[36px] text-center">{Math.round(zoomLevel * 100)}%</span>
          <button onClick={onZoomIn} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors" title="Zoom in"><ZoomIn size={16} className="text-gray-600" /></button>
          <button onClick={onResetView} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors" title="Reset view"><Maximize2 size={16} className="text-gray-600" /></button>
        </div>
      </div>
    </div>
  );
}

// ── Mobile floating zoom controls ──
export function MobileZoomControls({ zoomLevel, onZoomIn, onZoomOut, onResetView }) {
  return (
    <div className="absolute bottom-4 left-4 z-20 flex flex-col items-center gap-1 sm:hidden">
      <button onClick={onZoomIn} className="w-10 h-10 rounded-full bg-white shadow-md border border-gray-200 flex items-center justify-center active:bg-gray-100" title="Zoom in"><ZoomIn size={18} className="text-gray-700" /></button>
      <span className="text-[10px] text-gray-500 font-medium">{Math.round(zoomLevel * 100)}%</span>
      <button onClick={onZoomOut} className="w-10 h-10 rounded-full bg-white shadow-md border border-gray-200 flex items-center justify-center active:bg-gray-100" title="Zoom out"><ZoomOut size={18} className="text-gray-700" /></button>
      <button onClick={onResetView} className="w-10 h-10 rounded-full bg-white shadow-md border border-gray-200 flex items-center justify-center active:bg-gray-100 mt-1" title="Reset view"><Maximize2 size={18} className="text-gray-700" /></button>
    </div>
  );
}